import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getPackages, getRFQs, createRFQ, approveRFQ, sendRFQ, sendRFQReminder, getSuppliers } from '../utils/api';
import LifecyclePill from '../components/LifecyclePill';

function dueDateText(dateStr) {
  if (!dateStr) return { text: '—', color: 'var(--color-text-light)', overdue: false };
  const due = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.round((due - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { text: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`, color: 'var(--color-error)', overdue: true };
  if (diffDays === 0) return { text: 'Due today', color: 'var(--color-warning)', overdue: false };
  return { text: `${diffDays} day${diffDays !== 1 ? 's' : ''} remaining`, color: 'var(--color-success)', overdue: false };
}

export default function RFQPage() {
  const { selectedProject, addToast } = useOutletContext();
  const [packages, setPackages] = useState([]);
  const [rfqs, setRfqs] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [selectedRfqs, setSelectedRfqs] = useState([]);
  const [reminderSent, setReminderSent] = useState({});
  const [showNewRFQ, setShowNewRFQ] = useState(false);
  const [newRFQForm, setNewRFQForm] = useState({ supplier_ids: [], due_date: '' });
  const [creatingRFQ, setCreatingRFQ] = useState(false);
  const [approvingRFQ, setApprovingRFQ] = useState(false);
  const [sendingRFQ, setSendingRFQ] = useState(false);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedProject?.id) loadAll();
  }, [selectedProject]);

  async function loadAll() {
    setLoading(true);
    try {
      const [pkgsData, suppliersData] = await Promise.all([
        getPackages(selectedProject.id),
        getSuppliers()
      ]);

      // Show all packages — not just RFQ stages
      const pkgs = pkgsData || [];
      setPackages(pkgs);
      setSuppliers(suppliersData || []);

      if (pkgs.length > 0 && !selectedPkg) setSelectedPkg(pkgs[0]);

      // Load RFQs for all packages
      const rfqData = {};
      for (const pkg of pkgs) {
        try {
          rfqData[pkg.id] = await getRFQs(pkg.id);
        } catch { rfqData[pkg.id] = []; }
      }
      setRfqs(rfqData);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleCreateRFQ() {
    if (!selectedPkg || !newRFQForm.due_date) {
      setMessage({ type: 'error', text: 'Select a due date' });
      return;
    }
    setCreatingRFQ(true);
    try {
      await createRFQ({
        package_id: selectedPkg.id,
        supplier_ids: newRFQForm.supplier_ids.map(Number),
        due_date: newRFQForm.due_date
      });
      setShowNewRFQ(false);
      setNewRFQForm({ supplier_ids: [], due_date: '' });
      setMessage({ type: 'success', text: '✦ AI drafted RFQ — ready for your approval' });
      await loadAll();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setCreatingRFQ(false);
  }

  async function handleApproveRFQ(rfqId) {
    setApprovingRFQ(rfqId);
    try {
      await approveRFQ(rfqId, 'Approved');
      setMessage({ type: 'success', text: 'RFQ approved — ready to send' });
      await loadAll();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setApprovingRFQ(false);
  }

  async function handleSendRFQ(rfqId) {
    setSendingRFQ(rfqId);
    try {
      await sendRFQ(rfqId);
      setMessage({ type: 'success', text: 'RFQ sent to all suppliers' });
      await loadAll();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
    setSendingRFQ(false);
  }

  async function handleRemind(rfqId, supplierId) {
    try {
      await sendRFQReminder(rfqId, supplierId);
      setReminderSent(prev => ({ ...prev, [`${rfqId}-${supplierId}`]: true }));
    } catch (err) { console.error(err); }
  }

  const pkgRfqs = selectedPkg ? (rfqs[selectedPkg.id] || []) : [];
  const latestRfq = pkgRfqs[0] || null;
  const recipients = latestRfq?.recipients || [];
  const responded = recipients.filter(r => r?.status === 'responded').length;

  if (loading) return <div className="page"><div className="empty-state"><div className="spinner" /></div></div>;

  if (!selectedProject?.id) return (
    <div className="page">
      <div className="empty-state card" style={{ padding: 48 }}>
        <div className="empty-state-icon">✉</div>
        <h3>No project selected</h3>
      </div>
    </div>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">RFQ & Responses</div>
          <div className="page-subtitle">Manage supplier outreach and track responses</div>
        </div>
        <div className="page-actions">
          {selectedPkg && (
            <button onClick={() => setShowNewRFQ(true)} className="btn btn-primary">
              ✦ Draft RFQ with AI
            </button>
          )}
        </div>
      </div>

      {message && (
        <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', fontSize: '0.875rem', fontWeight: 500, marginBottom: 16, background: message.type === 'success' ? '#ECFDF5' : '#FEF2F2', color: message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)', border: `1px solid ${message.type === 'success' ? 'var(--color-success)' : 'var(--color-error)'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5, fontSize: '1rem' }}>✕</button>
        </div>
      )}

      {packages.length === 0 ? (
        <div className="empty-state card" style={{ padding: 48 }}>
          <div className="empty-state-icon">✉</div>
          <h3>No packages yet</h3>
          <p>Create packages first to send RFQs</p>
          <button onClick={() => navigate('/packages')} className="btn btn-primary">Go to Packages →</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16 }}>

          {/* Package list */}
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Packages ({packages.length})</span>
            </div>
            {packages.map(pkg => {
              const pkgRfqList = rfqs[pkg.id] || [];
              const isActive = selectedPkg?.id === pkg.id;
              const hasRfq = pkgRfqList.length > 0;
              const latestState = pkgRfqList[0]?.status;
              const state = pkg.lifecycle_state || 'draft_scope';

              return (
                <div key={pkg.id} onClick={() => setSelectedPkg(pkg)}
                  style={{
                    padding: '12px 16px', cursor: 'pointer',
                    background: isActive ? 'var(--color-teal-light)' : 'transparent',
                    borderLeft: `3px solid ${isActive ? 'var(--color-teal)' : 'transparent'}`,
                    borderBottom: '1px solid var(--color-border)',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--color-background)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{pkg.name}</div>
                    <LifecyclePill state={state} />
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                    {hasRfq ? `RFQ ${latestState}` : 'No RFQ yet'}
                    {pkgRfqList.length > 0 && ` · ${pkgRfqList[0]?.recipients?.filter(r => r?.status === 'responded').length || 0}/${pkgRfqList[0]?.recipients?.length || 0} responded`}
                  </div>
                </div>
              );
            })}
          </div>

          {/* RFQ detail panel */}
          {selectedPkg && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Package header */}
              <div className="card">
                <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 4 }}>{selectedPkg.name}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <LifecyclePill state={selectedPkg.lifecycle_state} size="lg" />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>{selectedPkg.trade_category}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => navigate(`/packages/${selectedPkg.id}`)} className="btn btn-secondary btn-sm">
                      Open Package →
                    </button>
                    <button onClick={() => setShowNewRFQ(true)} className="btn btn-primary btn-sm">
                      ✦ Draft RFQ
                    </button>
                  </div>
                </div>
              </div>

              {/* New RFQ form */}
              {showNewRFQ && (
                <div className="card">
                  <div className="card-header">
                    <div>
                      <span style={{ fontWeight: 600 }}>Draft RFQ with AI</span>
                      <span className="ai-badge" style={{ marginLeft: 8 }}>✦ AI Will Draft</span>
                    </div>
                    <button onClick={() => setShowNewRFQ(false)} className="btn btn-ghost btn-sm">✕</button>
                  </div>
                  <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ padding: '12px 14px', background: 'var(--color-teal-light)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-teal)', fontSize: '0.8125rem', color: 'var(--color-teal)' }}>
                      ✦ AI will read the package scope and items to draft a professional RFQ email and scope sheet. You'll review and approve before anything is sent.
                    </div>

                    <div className="form-group">
                      <label className="form-label">RFQ Due Date *</label>
                      <input type="date" className="form-input" value={newRFQForm.due_date}
                        onChange={e => setNewRFQForm({ ...newRFQForm, due_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        style={{ maxWidth: 200 }}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Select Suppliers ({newRFQForm.supplier_ids.length} selected)</label>
                      {suppliers.length === 0 ? (
                        <div style={{ padding: '12px 14px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.875rem', color: 'var(--color-text-mid)' }}>
                          No suppliers yet. <span style={{ color: 'var(--color-teal)', cursor: 'pointer', fontWeight: 600 }} onClick={() => { setShowNewRFQ(false); navigate('/suppliers'); }}>Add suppliers first →</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                          {suppliers.map(s => {
                            const isSelected = newRFQForm.supplier_ids.includes(s.id.toString());
                            return (
                              <div key={s.id}
                                onClick={() => setNewRFQForm(prev => ({
                                  ...prev,
                                  supplier_ids: isSelected
                                    ? prev.supplier_ids.filter(x => x !== s.id.toString())
                                    : [...prev.supplier_ids, s.id.toString()]
                                }))}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 10,
                                  padding: '10px 14px',
                                  border: `1px solid ${isSelected ? 'var(--color-teal)' : 'var(--color-border)'}`,
                                  borderRadius: 'var(--radius-md)', cursor: 'pointer',
                                  background: isSelected ? 'var(--color-teal-light)' : 'var(--color-surface)',
                                }}
                              >
                                <input type="checkbox" checked={isSelected} readOnly style={{ accentColor: 'var(--color-teal)' }} />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{s.name}</div>
                                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                                    {s.trade_categories?.[0]} · {s.contact_email || 'No email'}
                                  </div>
                                </div>
                                {s.approved_status === 'approved' && (
                                  <span className="badge badge-success" style={{ fontSize: '0.6875rem' }}>Approved</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleCreateRFQ} disabled={creatingRFQ || !newRFQForm.due_date} className="btn btn-primary">
                        {creatingRFQ ? '✦ AI Drafting...' : '✦ Generate RFQ Draft'}
                      </button>
                      <button onClick={() => setShowNewRFQ(false)} className="btn btn-secondary">Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {/* RFQ list */}
              {pkgRfqs.length === 0 ? (
                <div className="empty-state card" style={{ padding: 40 }}>
                  <div className="empty-state-icon">✉</div>
                  <h3>No RFQs for this package</h3>
                  <p>Use AI to draft and send an RFQ to suppliers</p>
                  <button onClick={() => setShowNewRFQ(true)} className="btn btn-primary">✦ Draft RFQ with AI</button>
                </div>
              ) : (
                pkgRfqs.map(rfq => {
                  const rfqRecipients = rfq.recipients || [];
                  const respondedCount = rfqRecipients.filter(r => r?.status === 'responded').length;
                  const allResponded = rfqRecipients.length > 0 && respondedCount === rfqRecipients.length;
                  const due = dueDateText(rfq.due_date);

                  return (
                    <div key={rfq.id} className="card">
                      {/* RFQ Header */}
                      <div className="card-header">
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{rfq.subject || 'RFQ Draft'}</div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span className={`badge ${rfq.status === 'sent' ? 'badge-success' : rfq.status === 'approved' ? 'badge-info' : 'badge-warning'}`}>
                              {rfq.status}
                            </span>
                            {rfq.due_date && (
                              <span style={{ fontSize: '0.75rem', fontWeight: 500, color: due.color }}>{due.text}</span>
                            )}
                          </div>
                        </div>

                        {/* Action buttons based on status */}
                        <div style={{ display: 'flex', gap: 8 }}>
                          {rfq.status === 'draft' && (
                            <button onClick={() => handleApproveRFQ(rfq.id)} disabled={approvingRFQ === rfq.id} className="btn btn-approve btn-sm">
                              {approvingRFQ === rfq.id ? '...' : '✓ Approve RFQ'}
                            </button>
                          )}
                          {rfq.status === 'approved' && (
                            <button onClick={() => handleSendRFQ(rfq.id)} disabled={sendingRFQ === rfq.id} className="btn btn-primary btn-sm">
                              {sendingRFQ === rfq.id ? 'Sending...' : '✉ Send to Suppliers'}
                            </button>
                          )}
                          {rfq.status === 'sent' && allResponded && (
                            <button onClick={() => navigate('/bid-leveling')} className="btn btn-primary btn-sm">
                              Compare Quotes →
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Approval gate for draft */}
                      {rfq.status === 'draft' && (
                        <div style={{ margin: '0 20px 16px', padding: '14px 16px', background: 'var(--color-warning-bg)', border: '2px solid var(--color-warning)', borderRadius: 'var(--radius-md)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <span className="ai-badge">✦ AI Prepared</span>
                            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-warning)', textTransform: 'uppercase' }}>Pending Your Approval</span>
                          </div>
                          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-dark)', marginBottom: 10 }}>
                            AI has drafted this RFQ. Review the content below and approve before sending to suppliers. Nothing has been sent yet.
                          </p>
                          <button onClick={() => handleApproveRFQ(rfq.id)} disabled={approvingRFQ === rfq.id} className="btn btn-approve btn-sm">
                            {approvingRFQ === rfq.id ? '...' : '✓ Approve & Proceed to Send'}
                          </button>
                        </div>
                      )}

                      {/* RFQ Body preview */}
                      {rfq.body && (
                        <div style={{ margin: '0 20px 16px' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>RFQ Content</div>
                          <div style={{ padding: '14px 16px', background: 'var(--color-background)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.8125rem', lineHeight: 1.7, color: 'var(--color-text-dark)', whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto', fontFamily: 'var(--font-mono)' }}>
                            {rfq.body}
                          </div>
                        </div>
                      )}

                      {/* Recipients */}
                      {rfqRecipients.length > 0 && (
                        <div style={{ margin: '0 20px 20px' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                            Suppliers ({respondedCount}/{rfqRecipients.length} responded)
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {rfqRecipients.map(r => {
                              if (!r) return null;
                              const recipientDue = dueDateText(rfq.due_date);
                              return (
                                <div key={r.id} style={{
                                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                                  background: 'var(--color-background)', borderRadius: 'var(--radius-md)',
                                  border: '1px solid var(--color-border)',
                                  borderLeft: `3px solid ${r.status === 'responded' ? 'var(--color-success)' : recipientDue.overdue ? 'var(--color-error)' : 'var(--color-border)'}`,
                                }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{r.supplier_name || `Supplier #${r.supplier_id}`}</div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                                      {r.supplier_email || '—'}
                                      {r.sent_at && ` · Sent ${new Date(r.sent_at).toLocaleDateString()}`}
                                      {r.responded_at && ` · Responded ${new Date(r.responded_at).toLocaleDateString()}`}
                                    </div>
                                  </div>
                                  <span className={`badge ${r.status === 'responded' ? 'badge-success' : r.status === 'sent' ? 'badge-warning' : 'badge-neutral'}`}>
                                    {r.status}
                                  </span>
                                  {r.status === 'sent' && (
                                    <button
                                      onClick={() => handleRemind(rfq.id, r.supplier_id)}
                                      className="btn btn-secondary btn-sm"
                                      style={{ fontSize: '0.75rem', color: reminderSent[`${rfq.id}-${r.supplier_id}`] ? 'var(--color-success)' : undefined }}
                                    >
                                      {reminderSent[`${rfq.id}-${r.supplier_id}`] ? '✓ Reminded' : 'Remind'}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* All responded prompt */}
                          {allResponded && rfq.status === 'sent' && (
                            <div style={{ marginTop: 12, padding: '14px 16px', background: '#ECFDF5', border: '1px solid var(--color-success)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--color-success)', fontSize: '0.875rem' }}>✓ All quotes received</div>
                                <div style={{ fontSize: '0.75rem', color: '#166534', marginTop: 2 }}>Ready to run bid leveling and compare quotes</div>
                              </div>
                              <button onClick={() => navigate('/bid-leveling')} className="btn btn-primary btn-sm">
                                Level Bids →
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}